import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { encodeLinkKey, generateLinkKey, sealSecret, toBase64Url } from '@sss/crypto';
import { CHALLENGE_ACTIONS } from '@sss/types';
import FlameMark from '../components/Brand/FlameMark';
import HumanCheck from '../components/Secret/HumanCheck';
import SecretCreated from '../components/Secret/SecretCreated';
import {
  EXPIRY_OPTIONS,
  MAX_SECRET_BYTES,
  VIEW_OPTIONS,
  checkSecretText,
  expirySeconds,
  formatKilobytes,
  utf8ByteLength,
  type ExpiryKey,
  type ViewOption,
} from '../lib/secretForm';
import { useHumanCheck } from '../hooks/useHumanCheck';
import { usePageTitle } from '../hooks/usePageTitle';
import { buildSecretLink, buildStatusLink } from '../lib/secretLinks';
import { SecretsApiError, createSecret } from '../lib/secretsApi';
import ui from '../styles/ui.module.css';
import styles from './CreatePage.module.css';

const USE_CASES = ['passwords', 'documents', 'devices'] as const;

interface Created {
  secretLink: string;
  statusLink: string;
  expiresAt: string;
  views: number;
  passphrase: string;
}

/** /new — a burn link: encrypted here, stored until read (docs/adr/0006). */
export default function CreatePage() {
  const { t } = useTranslation();
  usePageTitle(t('pageTitle.burnLink'));

  return (
    <main className={styles.page}>
      <div className={styles.grid}>
        <section className={styles.hero} aria-labelledby="create-title">
          <h1 id="create-title" className={styles.title}>
            {t('create.titleLine1')}
            <br />
            {t('create.titleLine2')}
            <br />
            <span className={styles.titleAccent}>{t('create.titleLine3')}</span>
          </h1>
          <p className={styles.lead}>{t('create.lead')}</p>
          <ol className={styles.lifecycle} aria-label={t('create.lifecycleLabel')}>
            <li>{t('create.lifecycle.encrypted')}</li>
            <li aria-hidden="true" className={styles.fuse} />
            <li>{t('create.lifecycle.sent')}</li>
            <li aria-hidden="true" className={styles.fuse} />
            <li>{t('create.lifecycle.read')}</li>
            <li aria-hidden="true" className={`${styles.fuse} ${styles.fuseLit}`} />
            <li className={styles.burned}>{t('create.lifecycle.burned')}</li>
          </ol>
          <p className={styles.codeLink}>
            <Link to="/how-it-works#code">{t('create.readTheCode')}</Link>
          </p>
          <p className={styles.codeLink}>
            <Link to="/live">{t('create.liveLink')}</Link>
          </p>
        </section>
        <BurnLinkPanel />
      </div>

      <section className={styles.useCases} aria-labelledby="use-cases-title">
        <h2 id="use-cases-title" className={styles.useCasesTitle}>
          {t('create.useCases.title')}
        </h2>
        <ul className={styles.useCaseList}>
          {USE_CASES.map((key) => (
            <li key={key} className={styles.useCase}>
              <h3 className={styles.useCaseTitle}>{t(`create.useCases.${key}.title`)}</h3>
              <p>{t(`create.useCases.${key}.body`)}</p>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

/** The burn-link form, then the link. Used by /new and by the send wizard. */
export function BurnLinkPanel() {
  const { t } = useTranslation();
  const [secret, setSecret] = useState('');
  const [views, setViews] = useState<ViewOption>(1);
  const [expiry, setExpiry] = useState<ExpiryKey>('1d');
  const [passphrase, setPassphrase] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  // Only a problem with the text itself marks the field invalid; a failed check or request doesn't.
  const [secretInvalid, setSecretInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);
  const humanCheck = useHumanCheck(CHALLENGE_ACTIONS.createSecret);

  const bytes = utf8ByteLength(secret);
  const nearLimit = bytes > MAX_SECRET_BYTES * 0.8;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const check = checkSecretText(secret);
    setSecretInvalid(Boolean(check));
    if (check) {
      setProblem(t(`create.errors.${check}`, { max: formatKilobytes(MAX_SECRET_BYTES) }));
      return;
    }
    const turnstileToken = humanCheck.take();
    if (!turnstileToken) {
      setProblem(t(humanCheck.state === 'failed' || humanCheck.state === 'unavailable' ? 'create.errors.checkFailed' : 'create.errors.checkPending'));
      return;
    }
    setProblem(null);
    setBusy(true);
    try {
      // Encrypt here; only the sealed payload goes to the server. The key
      // goes into the link fragment, which is never sent anywhere.
      const linkKey = generateLinkKey();
      const sealed = await sealSecret(secret, linkKey, { passphrase: passphrase || undefined });
      const response = await createSecret({ payload: toBase64Url(sealed), maxViews: views, expiresInSeconds: expirySeconds(expiry), turnstileToken });
      const origin = window.location.origin;
      setCreated({
        secretLink: buildSecretLink(origin, response.id, encodeLinkKey(linkKey)),
        statusLink: buildStatusLink(origin, response.id, response.senderToken),
        expiresAt: response.expiresAt,
        views,
        // Shown again only on its own share path, so it can go by a different channel from the link.
        passphrase,
      });
      // Don't keep the plaintext around once it's sealed and stored.
      setSecret('');
      setPassphrase('');
    } catch (err) {
      const type = err instanceof SecretsApiError ? err.type : 'other';
      const known = type === 'rateLimited' || type === 'tooLarge' || type === 'network' || type === 'challengeFailed';
      setProblem(t(`create.errors.${known ? type : 'failed'}`, { max: formatKilobytes(MAX_SECRET_BYTES) }));
    } finally {
      setBusy(false);
    }
  }

  function startAgain() {
    setCreated(null);
    setViews(1);
    setExpiry('1d');
  }

  return created ? (
    <div className={styles.result}>
      <SecretCreated {...created} onAnother={startAgain} />
    </div>
  ) : (
    <form className={`${ui.panel} ${styles.form}`} onSubmit={onSubmit} noValidate aria-labelledby="create-form-title" aria-busy={busy}>
      <div className={styles.formHeader}>
        <h2 id="create-form-title" className={styles.formTitle}>
          {t('create.form.title')}
        </h2>
        <span className={ui.badge}>{t('create.form.badge')}</span>
      </div>

      <label className={ui.field}>
        {t('create.form.secretLabel')}
        <textarea
          rows={6}
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder={t('create.form.secretPlaceholder')}
          aria-describedby={secretInvalid ? 'secret-size create-problem' : 'secret-size'}
          aria-invalid={secretInvalid || undefined}
          autoComplete="off"
          spellCheck={false}
          className={styles.secret}
        />
      </label>
      <p id="secret-size" className={`${ui.hint} ${styles.size}`} aria-live="polite">
        {nearLimit ? t('create.form.size', { used: formatKilobytes(bytes), max: formatKilobytes(MAX_SECRET_BYTES) }) : t('create.form.sizeHint', { max: formatKilobytes(MAX_SECRET_BYTES) })}
      </p>

      <div className={styles.row}>
        <label className={ui.field}>
          {t('create.form.viewsLabel')}
          <select value={views} onChange={(e) => setViews(Number(e.target.value) as ViewOption)}>
            {VIEW_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {t('create.form.views', { count: n })}
              </option>
            ))}
          </select>
        </label>
        <label className={ui.field}>
          {t('create.form.expiryLabel')}
          <select value={expiry} onChange={(e) => setExpiry(e.target.value as ExpiryKey)}>
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {t(`create.expiry.${o.key}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className={ui.field}>
        {t('create.form.passphraseLabel')}
        <input
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          placeholder={t('create.form.passphrasePlaceholder')}
          autoComplete="new-password"
        />
        <span className={ui.hint}>{t('create.form.passphraseHint')}</span>
      </label>

      <HumanCheck check={humanCheck} />

      {problem && (
        <p role="alert" id="create-problem" className={ui.error}>
          {problem}
        </p>
      )}

      <button type="submit" className={ui.button} disabled={busy}>
        <FlameMark size={18} tone="onAccent" />
        {busy ? t('create.form.submitting') : t('create.form.submit')}
      </button>
      <p className={`${ui.hint} ${styles.terms}`}>{t('create.form.terms', { max: formatKilobytes(MAX_SECRET_BYTES) })}</p>
    </form>
  );
}

